/*────────────────────────────────────────────────────────────────────────────
  Calendar Utilities - Helper functions for calendar operations
────────────────────────────────────────────────────────────────────────────*/
import {
  format,
  addDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { ScheduleSlot, ScheduleType, DEFAULT_TIME_SLOTS } from './scheduleTypes';

// Date formatting utilities
export const formatDateKey = (date: Date): string => format(date, 'yyyy-MM-dd');

export const formatDisplayDate = (date: Date): string => format(date, 'EEEE, MMMM d, yyyy');

export const formatDisplayTime = (time: string): string => time;

// Shared timeline/day-boundary helpers used by scheduling + ticket timelines.
export const DAY_IN_MS = 24 * 60 * 60 * 1000;

export const toLocalDayStart = (value: Date | string | number): Date => startOfDay(new Date(value));

export const toLocalDayEnd = (value: Date | string | number): Date => endOfDay(new Date(value));

export const toLocalDayStartMs = (value: Date | string | number): number =>
  toLocalDayStart(value).getTime();

export const toLocalDayEndMs = (value: Date | string | number): number =>
  toLocalDayEnd(value).getTime();

export const toLocalDayStartIso = (value: Date | string | number): string =>
  new Date(toLocalDayStartMs(value)).toISOString();

export const toLocalDayEndIso = (value: Date | string | number): string =>
  new Date(toLocalDayEndMs(value)).toISOString();

export const roundDurationToWholeDaysMs = (durationMs: number): number =>
  Math.round(durationMs / DAY_IN_MS) * DAY_IN_MS;

// Slot key generation for legacy compatibility
export const createLegacySlotKey = (date: Date, time: string): string => 
  `${formatDateKey(date)}|${time}`;

export const createSlotKey = (date: Date, time: string, type: ScheduleType): string => 
  `${formatDateKey(date)}|${time}|${type}`;

// Calendar matrix generation
export const generateCalendarDays = (month: Date): Date[] => {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  const days: Date[] = [];
  
  for (let d = start; d <= end; d = addDays(d, 1)) {
    days.push(d);
  }
  
  return days;
};

// Slot filtering and grouping
export const getSlotsByDate = (slots: ScheduleSlot[], date: Date): ScheduleSlot[] => {
  const dateKey = formatDateKey(date);
  return slots.filter(slot => slot.date === dateKey && slot.isActive);
};

export const getSlotsByType = (slots: ScheduleSlot[], type: ScheduleType): ScheduleSlot[] => {
  return slots.filter(slot => slot.type === type && slot.isActive);
};

export const getSlotsByDateAndType = (
  slots: ScheduleSlot[], 
  date: Date, 
  type: ScheduleType
): ScheduleSlot[] => {
  const dateKey = formatDateKey(date);
  return slots.filter(slot => 
    slot.date === dateKey && 
    slot.type === type && 
    slot.isActive
  );
};

// Get unique types for a date
export const getTypesForDate = (slots: ScheduleSlot[], date: Date): ScheduleType[] => {
  const dateSlots = getSlotsByDate(slots, date);
  const typeSet = new Set(dateSlots.map(slot => slot.type));
  return Array.from(typeSet);
};

// Check if date has any availability
export const hasAvailability = (slots: ScheduleSlot[], date: Date): boolean => {
  return getSlotsByDate(slots, date).length > 0;
};

// Check if specific time slot exists
export const hasTimeSlot = (
  slots: ScheduleSlot[], 
  date: Date, 
  time: string, 
  type: ScheduleType
): boolean => {
  const dateKey = formatDateKey(date);
  return slots.some(slot => 
    slot.date === dateKey && 
    slot.time === time && 
    slot.type === type && 
    slot.isActive
  );
};

// Generate unique ID for new slots
export const generateSlotId = (): string => {
  return `slot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Data migration utilities
export const migrateLegacyAvailability = (legacyData: string[]): ScheduleSlot[] => {
  return legacyData.map((slotKey, index) => {
    const [date, time] = slotKey.split('|');
    return {
      id: `migrated-${index}`,
      date,
      time,
      type: 'general_meeting' as ScheduleType,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };
  });
};

// Local storage management
const LS_KEY = 'cybard_schedule_slots';
const LS_LEGACY_KEY = 'cybard_availability';

export const loadSlotsFromStorage = (): ScheduleSlot[] => {
  try {
    // Try to load new format first
    const newFormatData = localStorage.getItem(LS_KEY);
    if (newFormatData) {
      const parsed = JSON.parse(newFormatData);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
    
    // Fallback to legacy format
    const legacyData = localStorage.getItem(LS_LEGACY_KEY);
    if (legacyData) {
      const parsed = JSON.parse(legacyData);
      if (Array.isArray(parsed)) {
        const migratedSlots = migrateLegacyAvailability(parsed);
        // Save in new format
        saveSlotsToStorage(migratedSlots);
        // Remove legacy data
        localStorage.removeItem(LS_LEGACY_KEY);
        return migratedSlots;
      }
    }
    
    return [];
  } catch (error) {
    console.warn('Error loading schedule data from localStorage:', error);
    return [];
  }
};

export const saveSlotsToStorage = (slots: ScheduleSlot[]): void => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(slots));
  } catch (error) {
    console.error('Error saving schedule data to localStorage:', error);
  }
};

// Statistics calculation
export interface ScheduleStats {
  totalSlots: number;
  consultationSlots: number;
  clientMeetingSlots: number;
  workAvailabilitySlots: number; // retained for backward compatibility
  daysWithAvailability: number;
  avgSlotsPerDay: number;
}

export const calculateScheduleStats = (slots: ScheduleSlot[]): ScheduleStats => {
  const activeSlots = slots.filter(slot => slot.isActive);
  
  const consultationSlots = activeSlots.filter(slot => slot.type === 'consultation').length;
  const clientMeetingSlots = activeSlots.filter(slot =>
    ['intro_call', 'general_meeting', 'weekly_sync', 'monthly_sync', 'toe_touch'].includes(slot.type)
  ).length;
  const workAvailabilitySlots = 0;
  
  const uniqueDates = new Set(activeSlots.map(slot => slot.date));
  const daysWithAvailability = uniqueDates.size;
  
  return {
    totalSlots: activeSlots.length,
    consultationSlots,
    clientMeetingSlots,
    workAvailabilitySlots,
    daysWithAvailability,
    avgSlotsPerDay: daysWithAvailability > 0 ? activeSlots.length / daysWithAvailability : 0
  };
};

// Time slot utilities
export const parseTimeSlot = (timeString: string): { hour: number; minute: number; period: 'AM' | 'PM' } => {
  const match = timeString.match(/^(\d{1,2}):(\d{2})\s+(AM|PM)$/);
  if (!match) {
    throw new Error(`Invalid time format: ${timeString}`);
  }
  
  return {
    hour: parseInt(match[1], 10),
    minute: parseInt(match[2], 10),
    period: match[3] as 'AM' | 'PM'
  };
};

export const formatTimeSlot = (hour: number, minute: number, period: 'AM' | 'PM'): string => {
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

export const sortTimeSlots = (timeSlots: string[]): string[] => {
  return [...timeSlots].sort((a, b) => {
    const timeA = parseTimeSlot(a);
    const timeB = parseTimeSlot(b);
    
    // Convert to 24-hour format for comparison
    const hourA = timeA.period === 'PM' && timeA.hour !== 12 ? timeA.hour + 12 : 
                  timeA.period === 'AM' && timeA.hour === 12 ? 0 : timeA.hour;
    const hourB = timeB.period === 'PM' && timeB.hour !== 12 ? timeB.hour + 12 : 
                  timeB.period === 'AM' && timeB.hour === 12 ? 0 : timeB.hour;
    
    if (hourA !== hourB) {
      return hourA - hourB;
    }
    
    return timeA.minute - timeB.minute;
  });
};

// Validation utilities
export const validateScheduleSlot = (slot: Partial<ScheduleSlot>): string[] => {
  const errors: string[] = [];
  
  if (!slot.date) {
    errors.push('Date is required');
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(slot.date)) {
    errors.push('Date must be in YYYY-MM-DD format');
  }
  
  if (!slot.time) {
    errors.push('Time is required');
  } else if (!DEFAULT_TIME_SLOTS.includes(slot.time)) {
    errors.push('Invalid time slot');
  }
  
  if (!slot.type) {
    errors.push('Schedule type is required');
  } else if (
    ![
      'consultation',
      'intro_call',
      'general_meeting',
      'weekly_sync',
      'monthly_sync',
      'toe_touch',
    ].includes(slot.type)
  ) {
    errors.push('Invalid schedule type');
  }
  
  return errors;
};
